import Crop from "@/components/Crop";
import { useCurrentUser } from "@/services/hooks";
import { uploadUserFile } from "@/services/upload";
import { Edit } from "@mui/icons-material";
import {
  Box,
  CardMedia,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import AlertSnackbar from "@/components/AlertSnackbar";

export default function CoverImage() {
  const { data: currentUser, isLoading } = useCurrentUser();
  const [editing, setEditing] = useState(false);
  const [file, setFile] = useState(null);
  const queryClient = useQueryClient();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const metaKey = "cover";

  const imgUrl = file
    ? URL.createObjectURL(file)
    : currentUser?.meta?.[metaKey];

  const mutation = useMutation({
    mutationFn: (blob) => uploadUserFile(blob, metaKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["current_user"] });
      setShowSnackbar(true);
    },
  });

  const handleFileChange = () => {
    const selectedFile = document.getElementById(metaKey).files[0];
    setFile(selectedFile);
    setEditing(true);
  };

  const onDone = (blob) => {
    mutation.mutate(blob);
    setFile(blob);
    setEditing(false);
  };

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        height={200}
        width={200}
        sx={{ borderRadius: "25px" }}
      />
    );
  }

  if (editing) {
    return (
      <Crop
        file={file}
        setFile={setFile}
        setEditing={setEditing}
        onDone={onDone}
      />
    );
  }

  return (
    <Box sx={{ width: "200px" }}>
      <AlertSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          src={imgUrl}
          sx={{
            borderRadius: "10px",
            aspectRatio: "1/1",
            opacity: mutation.isPending ? 0.5 : 1,
            transition: "opacity 0.2s ease-in-out",
          }}
        />

        <label htmlFor={metaKey}>
          <IconButton
            component="span"
            disabled={mutation.isPending}
            sx={{
              position: "absolute",
              bottom: 10,
              right: 10,
              opacity: 0.8,
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            {mutation.isPending ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <Edit />
            )}
          </IconButton>
        </label>
      </Box>
      <input
        accept="image/*"
        style={{ display: "none" }}
        id={metaKey}
        type="file"
        onChange={handleFileChange}
      />
      <Typography variant="caption" color="text.secondary" sx={{ my: 2 }}>
        Homepage Cover Photo
      </Typography>
    </Box>
  );
}
